/** @file Sets one bone's share of a vertex's weights: exact with auto-normalise, raw otherwise (W2-29 form). */
import { normalizeVertexWeights } from '@pose2d/commands/mesh/normalizeVertexWeights';
import type { Id } from '@pose2d/format/types';

type Influences = [Id, number][];

/**
 * The row with `bone` at share s (0..1), in the normal form mesh.setWeights stores. Auto-normalise: the other bones
 * are scaled to fill 1 − s, so `bone` ends at exactly s (a vertex weighted only to `bone` keeps 1: no other bone can
 * take the rest). Without it the raw weight of `bone` becomes s and the normalizer divides every bone by the sum.
 */
export function setShare(row: readonly (readonly [Id, number])[], bone: Id, s: number, autoNormalize: boolean): Influences {
  const others = row.filter(([b]) => b !== bone).map(([b, w]): [Id, number] => [b, w]);
  const rest = others.reduce((sum, [, w]) => sum + w, 0);
  const copy = (): Influences => row.map(([b, w]): [Id, number] => [b, w]);
  if (autoNormalize && !(rest > 0)) return s > 0 ? [[bone, 1]] : copy();
  const scaled = autoNormalize ? others.map(([b, w]): [Id, number] => [b, (w * (1 - s)) / rest]) : others;
  return normalizeVertexWeights([[bone, s], ...scaled]) ?? copy();
}
